import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../services/api";
import { useAuth } from "./AuthContext";

const ClubSettingsContext = createContext();

export const useClubSettings = () => {
  const context = useContext(ClubSettingsContext);
  if (!context) {
    throw new Error(
      "useClubSettings must be used within a ClubSettingsProvider"
    );
  }
  return context;
};

export const ClubSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Get clubId from URL params or user data
  const params = useParams();
  const clubId = params.clubId || user?.club_id;

  const loadClubSettings = useCallback(async () => {
    try {
      setLoading(true);
      const clubSettings = await apiService.getClubSettings(clubId);
      setSettings(clubSettings);
    } catch (error) {
      console.error("Failed to load club settings:", error);
      // Set default settings if failed to load
      setSettings({ evaluation_type: "skills" });
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    if (clubId && user) {
      loadClubSettings();
    } else {
      setLoading(false);
    }
  }, [clubId, user, loadClubSettings]);

  const updateSettings = async (newSettings) => {
    try {
      const updatedSettings = await apiService.updateClubSettings(
        clubId,
        newSettings
      );
      setSettings(updatedSettings);
      return updatedSettings;
    } catch (error) {
      console.error("Failed to update club settings:", error);
      throw error;
    }
  };

  const isSkillsBasedClub = () => {
    return settings?.evaluation_type === "skills" || !settings?.evaluation_type;
  };

  const isGroupsBasedClub = () => {
    return settings?.evaluation_type === "groups";
  };

  const value = {
    settings,
    loading,
    clubId,
    updateSettings,
    loadClubSettings,
    isSkillsBasedClub: isSkillsBasedClub(),
    isGroupsBasedClub: isGroupsBasedClub(),
    evaluationType: settings?.evaluation_type || "skills",
  };

  return (
    <ClubSettingsContext.Provider value={value}>
      {children}
    </ClubSettingsContext.Provider>
  );
};
